import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Token": t } });
      setRows(res.data);
      setAuthed(true);
    } catch (e) {
      setError("Invalid admin token.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-950 grid place-items-center px-4">
        <div className="w-full max-w-sm border border-white/10 bg-slate-900/50 p-8">
          <h1 className="font-display text-2xl text-white tracking-tight mb-2">AeroVaid Admin</h1>
          <p className="text-slate-400 text-sm mb-6">Enter the admin token to view enquiries.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="w-full h-12 bg-slate-950 border border-white/10 px-4 text-white rounded-sm mb-4 outline-none focus:border-white/30"
          />
          <button
            data-testid="admin-login-btn"
            onClick={() => load(token)}
            className="w-full h-12 bg-white text-slate-950 font-semibold rounded-sm hover:bg-slate-200 transition-colors"
          >
            {loading ? "Checking..." : "View Enquiries"}
          </button>
          {error && <p className="text-red-400 text-sm mt-4" data-testid="admin-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 sm:px-8 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl text-white tracking-tight">Enquiries</h1>
            <p className="text-slate-400 text-sm mt-1">{rows.length} total</p>
          </div>
          <button onClick={() => load(token)} data-testid="admin-refresh-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2 rounded-sm hover:bg-white/5 transition-colors text-sm">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {rows.length === 0 ? (
          <p className="text-slate-500">No enquiries yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map((r) => (
              <div key={r.id} className="border border-white/10 bg-slate-900/50 p-6" data-testid="admin-enquiry-row">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl text-white">{r.name}</h3>
                  <span className="label-mono text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-300">
                  <a href={`tel:${r.phone}`} className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {r.phone}</a>
                  {r.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {r.email}</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.course && <span className="text-xs border border-white/15 px-2 py-1 rounded-sm">{r.course}</span>}
                  {r.mode && <span className="text-xs border border-white/15 px-2 py-1 rounded-sm">{r.mode}</span>}
                </div>
                {r.message && <p className="mt-3 text-slate-400 text-sm leading-relaxed">{r.message}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
